import React, { useState } from 'react'
import {
  Steps,
  Form,
  Input,
  Button,
  message,
  Select,
  Row,
  Col,
  DatePicker,
  Checkbox
} from 'antd'
import styled from 'styled-components'
import api from '../utils/api'
import { categories } from '../utils/utils'
import dayjs from 'dayjs'
const { Step } = Steps

const StepFormContainer = styled.div`
  display: flex;
  margin: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 20px 0;
`

const FormContainer = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: 90%;
  max-width: 460px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px;
    width: 95%;
  }
`

const LogoContainer = styled.div`
  margin-bottom: 32px;
  text-align: center;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 20px;

  @media (max-width: 820px) {
    width: 60px;
    height: 60px;
  }
`

const Title = styled.h1`
  font-size: 24px;
  color: #1a1a1a;
  margin-bottom: 8px;
  font-weight: 600;
`

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
`

const PlanCard = styled.div`
  border: 1px solid ${(props) => (props.selected ? '#1890ff' : '#e8e8e8')};
  border-radius: 8px;
  padding: 15px;
  margin: 10px;
  cursor: pointer;
  background: ${(props) => (props.selected ? '#f0f7ff' : 'white')};
  transition: all 0.3s;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 40px;
  font-size: 16px;
  border-radius: 8px;
`

const DateRangeText = styled.div`
  color: #8c8c8c;
  font-size: 14px;
  margin-top: 4px;
  padding-left: 4px;
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

const RegisterPage = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [adminCredentials, setAdminCredentials] = useState({
    username: '',
    password: ''
  })
  const [businessDetails, setBusinessDetails] = useState({
    username: '',
    businessUsername: '',
    email: '',
    password: '',
    businessName: '',
    address: '',
    phone: '',
    description: '',
    plan: '',
    duration: '',
    serviceType: [],
    startDate: null,
    allowExistingOwner: false
  })
  const [error, setError] = useState('')
  const [planDateRange, setPlanDateRange] = useState('')
  const [form] = Form.useForm()

  const handleAdminLogin = async () => {
    try {
      await api.post(`/api/auth/login/admin`, adminCredentials)
      message.success('管理員登入成功')
      setCurrentStep(1)
    } catch (error) {
      message.error('管理員登入失敗')
      setError(error.response?.data?.error || '登入失敗')
    }
  }

  const handleRegisterBusiness = async () => {
    try {
      const formValues = await form.validateFields()
      const startDate = formValues.startDate
        ? dayjs.isDayjs(formValues.startDate)
          ? formValues.startDate.format('YYYY-MM-DD')
          : formValues.startDate
        : businessDetails.startDate
      const submitData = {
        username: formValues.username ?? businessDetails.username,
        businessUsername:
          formValues.businessUsername ?? businessDetails.businessUsername,
        email: formValues.email ?? businessDetails.email,
        password: formValues.password ?? businessDetails.password,
        businessName: formValues.businessName ?? businessDetails.businessName,
        address: formValues.address ?? businessDetails.address,
        phone: formValues.phone ?? businessDetails.phone,
        description:
          formValues.description ?? businessDetails.description ?? '',
        employeeType: businessDetails.employeeType ?? '設計師',
        plan: businessDetails.plan,
        subscriptionDuration: formValues.duration ?? businessDetails.duration,
        serviceTypes: Array.isArray(formValues.serviceType)
          ? formValues.serviceType
          : (businessDetails.serviceType ?? []),
        startDate,
        allowExistingOwner: !!(
          formValues.allowExistingOwner ?? businessDetails.allowExistingOwner
        )
      }

      const response = await api.post('/api/auth/register-business', submitData)

      if (response.data.success) {
        message.success('商家註冊成功')
        setCurrentStep(2)
      } else {
        message.error('商家註冊失敗')
        setError(response.data.message || '註冊失敗，請重試。')
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('請填寫所有必填欄位')
      } else {
        const errorMessage =
          error.response?.data?.message || '註冊失敗，請重試。'
        message.error(errorMessage)
        setError(errorMessage)
      }
    }
  }

  // 兩方案制：只販售一般方案（basic）與高階方案（brand）。
  // 商務方案（business）已停售，既有客戶權益不變，但不可再新購——
  // 它的權益是錯亂的（員工上限 20 位比高階方案多，卻沒有堂票、回訪提醒、
  // Google 日曆同步、招聘媒合，那些都是 requireSubscription('brand')）。
  //
  // 價格對齊 beauty-server/shared/models/PricingPlan.js 的 monthlyPrice，
  // 實際收費是由後端 getPlanPrice() 讀 PricingPlan 決定，此處僅為顯示。
  const plans = [
    { id: 'free', name: '試用版', price: '0' },
    { id: 'basic', name: '一般方案', price: '1400' },
    { id: 'brand', name: '高階方案', price: '3800' }
  ]

  const durations = [
    { value: '7d', label: '7天' },
    { value: '1', label: '1個月' },
    { value: '3', label: '3個月' },
    { value: '6', label: '6個月' },
    { value: '12', label: '1年' },
    { value: '36', label: '3年' }
  ]

  const calculateDateRange = (startDate, duration) => {
    if (!startDate || !duration) return ''

    const start = dayjs(startDate)
    const isDayDuration =
      typeof duration === 'string' && duration.toLowerCase().endsWith('d')
    const durationValue = isDayDuration
      ? Number(duration.slice(0, -1))
      : Number(duration)

    if (!durationValue || Number.isNaN(durationValue)) return ''

    const end = isDayDuration
      ? start.add(durationValue, 'day')
      : start.add(durationValue, 'month')
    return `${start.format('YYYY/MM/DD')} - ${end.format('YYYY/MM/DD')}`
  }

  const steps = [
    {
      title: '管理員登入',
      content: (
        <Form form={form} onFinish={handleAdminLogin}>
          <Form.Item
            name='username'
            rules={[{ required: true, message: '請輸入管理員帳號！' }]}
          >
            <Input
              placeholder='管理員帳號'
              value={adminCredentials.username}
              onChange={(e) =>
                setAdminCredentials({
                  ...adminCredentials,
                  username: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item
            name='password'
            rules={[{ required: true, message: '請輸入密碼！' }]}
          >
            <Input
              type='password'
              placeholder='密碼'
              value={adminCredentials.password}
              onChange={(e) =>
                setAdminCredentials({
                  ...adminCredentials,
                  password: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item>
            <StyledButton type='primary' htmlType='submit'>
              管理員登入
            </StyledButton>
          </Form.Item>
          {error && <p style={{ color: 'red' }}>{error}</p>}
        </Form>
      )
    },
    {
      title: '商家註冊',
      content: (
        <Form form={form} onFinish={handleRegisterBusiness} layout='vertical'>
          <Form.Item
            label='帳號'
            name='username'
            rules={[
              { required: true, message: '請輸入帳號！' },
              {
                pattern: /^[a-zA-Z0-9._]{1,20}$/,
                message: '帳號必須是1-20個字符，僅可包含英文、數字、底線和點'
              }
            ]}
          >
            <Input
              placeholder='請輸入帳號'
              onChange={(e) => {
                setBusinessDetails({
                  ...businessDetails,
                  username: e.target.value
                })
              }}
            />
          </Form.Item>

          <Form.Item
            label='分店網址代號（businessUsername）'
            name='businessUsername'
            rules={[
              { required: true, message: '請輸入分店網址代號！' },
              {
                pattern: /^[a-zA-Z0-9._-]{1,30}$/,
                message:
                  '分店網址代號需為1-30字元，只能包含英文、數字、點、底線、連字號'
              }
            ]}
          >
            <Input
              placeholder='例如：mybrand-branch2'
              onChange={(e) => {
                setBusinessDetails({
                  ...businessDetails,
                  businessUsername: e.target.value
                })
              }}
            />
          </Form.Item>

          <Form.Item
            label='服務類型'
            name='serviceType'
            rules={[{ required: true, message: '請選擇至少一個服務類型！' }]}
          >
            <Select
              mode='multiple'
              placeholder='請選擇服務類型'
              style={{ width: '100%' }}
            >
              {categories.map((category) => (
                <Select.Option key={category.type} value={category.type}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label='方案選擇' required>
            <Row gutter={[16, 16]}>
              {plans.map((plan) => (
                <Col xs={12} sm={12} md={12} key={plan.id}>
                  <PlanCard
                    selected={businessDetails.plan === plan.id}
                    onClick={() =>
                      setBusinessDetails({
                        ...businessDetails,
                        plan: plan.id
                      })
                    }
                  >
                    <h3>{plan.name}</h3>
                    <p>NT$ {plan.price}/月</p>
                  </PlanCard>
                </Col>
              ))}
            </Row>
          </Form.Item>
          <Form.Item
            label='方案生效日'
            name='startDate'
            rules={[{ required: true, message: '請選擇方案生效日！' }]}
          >
            <StyledDatePicker
              placeholder='請選擇方案生效日'
              format='YYYY-MM-DD'
              onChange={(date) => {
                const newStartDate = date ? date.format('YYYY-MM-DD') : null
                setBusinessDetails({
                  ...businessDetails,
                  startDate: newStartDate
                })
                setPlanDateRange(
                  calculateDateRange(newStartDate, businessDetails.duration)
                )
              }}
            />
          </Form.Item>

          <Form.Item label='方案期限' name='duration'>
            <Select
              placeholder='請選擇方案期限'
              onChange={(value) => {
                setBusinessDetails({
                  ...businessDetails,
                  duration: value
                })
                setPlanDateRange(
                  calculateDateRange(businessDetails.startDate, value)
                )
              }}
              value={businessDetails.duration}
            >
              {durations.map((duration) => (
                <Select.Option key={duration.value} value={duration.value}>
                  {duration.label}
                </Select.Option>
              ))}
            </Select>
            {planDateRange && (
              <DateRangeText>方案期間：{planDateRange}</DateRangeText>
            )}
          </Form.Item>

          <Form.Item
            name='email'
            rules={[{ required: true, message: '請輸入電子郵件！' }]}
          >
            <Input
              type='email'
              placeholder='電子郵件'
              value={businessDetails.email}
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  email: e.target.value
                })
              }
            />
          </Form.Item>

          <Form.Item
            name='allowExistingOwner'
            valuePropName='checked'
            initialValue={false}
          >
            <Checkbox
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  allowExistingOwner: e.target.checked
                })
              }
            >
              沿用既有店主帳號（同 email 開第二分店）
            </Checkbox>
          </Form.Item>
          <Form.Item
            name='password'
            rules={[{ required: true, message: '請輸入密碼！' }]}
          >
            <Input
              type='password'
              placeholder='密碼'
              value={businessDetails.password}
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  password: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item
            name='businessName'
            rules={[{ required: true, message: '請輸入商家名稱！' }]}
          >
            <Input
              type='text'
              placeholder='商家名稱'
              value={businessDetails.businessName}
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  businessName: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item
            name='address'
            rules={[{ required: true, message: '請輸入地址！' }]}
          >
            <Input
              type='text'
              placeholder='地址'
              value={businessDetails.address}
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  address: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item
            name='phone'
            rules={[{ required: true, message: '請輸入電話號碼！' }]}
          >
            <Input
              type='text'
              placeholder='電話'
              value={businessDetails.phone}
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  phone: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item name='description'>
            <Input.TextArea
              placeholder='描述'
              value={businessDetails.description}
              onChange={(e) =>
                setBusinessDetails({
                  ...businessDetails,
                  description: e.target.value
                })
              }
            />
          </Form.Item>
          <Form.Item>
            <StyledButton type='primary' htmlType='submit'>
              註冊商家
            </StyledButton>
          </Form.Item>
          {error && <p style={{ color: 'red' }}>{error}</p>}
        </Form>
      )
    },
    {
      title: '完成註冊',
      content: (
        <div>
          <div style={{ marginBottom: '24px' }}>商家註冊成功！</div>
          <Row gutter={16}>
            <Col span={12}>
              <StyledButton
                onClick={() => {
                  setCurrentStep(0)
                  form.resetFields()
                  setBusinessDetails({
                    username: '',
                    businessUsername: '',
                    email: '',
                    password: '',
                    businessName: '',
                    address: '',
                    phone: '',
                    description: '',
                    plan: '',
                    duration: '',
                    serviceType: [],
                    startDate: null,
                    allowExistingOwner: false
                  })
                  setPlanDateRange('')
                  setError('')
                }}
              >
                繼續註冊下一個
              </StyledButton>
            </Col>
            <Col span={12}>
              <StyledButton
                type='primary'
                onClick={() => {
                  window.location.href = '/login' // 假設登入頁的路徑是 /login
                }}
              >
                回到登入頁
              </StyledButton>
            </Col>
          </Row>
        </div>
      )
    }
  ]

  return (
    <StepFormContainer>
      <FormContainer>
        <LogoContainer>
          <Logo src='/saa.png' alt='Logo' />
          <Title>商家註冊系統</Title>
          <Subtitle>
            {currentStep === 0
              ? '請先進行管理員驗證'
              : currentStep === 1
                ? '填寫商家註冊信息'
                : '註冊完成'}
          </Subtitle>
        </LogoContainer>
        {steps[currentStep].content}
      </FormContainer>
    </StepFormContainer>
  )
}

export default RegisterPage
